import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  return (
    <header className="relative min-h-[min(100vh,860px)] flex items-center bg-ink text-white overflow-hidden py-[4.5rem] pb-[5.5rem]">
      {/* background gradients */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 78% 45%, rgba(201, 164, 92, 0.22) 0%, transparent 55%), radial-gradient(ellipse 50% 40% at 15% 85%, rgba(11, 143, 122, 0.14) 0%, transparent 50%), linear-gradient(155deg, #07111F 0%, #0C1A2C 48%, #091420 100%)",
        }}
      />
      {/* grid mesh */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(ellipse 75% 70% at 65% 50%, black, transparent)",
          WebkitMaskImage:
            "radial-gradient(ellipse 75% 70% at 65% 50%, black, transparent)",
        }}
      />

      <div className="container mx-auto max-w-[1140px] px-4 relative z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-12 items-center">
          {/* copy */}
          <div>
            <div className="fade-up-anim animate-fade-up font-figtree font-bold text-[clamp(2.8rem,6vw,4.6rem)] leading-[1.05] tracking-[-0.02em] mb-[1.15rem] text-white">
              One<span className="text-gold">DPDP</span>
            </div>
            <h1 className="fade-up-anim animate-fade-up [animation-delay:0.12s] font-bricolage text-[clamp(1.35rem,2.5vw,1.85rem)] font-medium leading-[1.35] text-white/[.92] max-w-[22em] mb-[1.1rem]">
              The AI-native compliance platform built for India's DPDP Act.
            </h1>
            <p className="fade-up-anim animate-fade-up [animation-delay:0.28s] text-[1.05rem] leading-[1.65] text-white/[.62] max-w-[30rem] mb-8">
              Run Gap Assessments, PIAs, ROPA, third-party risk and breach
              response from one system — then let AI draft policies, surface
              gaps and cut weeks of manual work into hours.
            </p>
            <div className="fade-up-anim animate-fade-up [animation-delay:0.42s] flex flex-wrap gap-[0.85rem]">
              <a
                href="#contact"
                className="bg-teal hover:bg-teal-bright text-white font-semibold rounded px-7 py-[0.7rem] text-[1.02rem] transition-all hover:-translate-y-px"
              >
                Request a demo
              </a>
              <Link
                to="/login"
                className="bg-transparent text-white/90 border border-white/[.28] hover:border-white hover:text-white hover:bg-white/[.06] font-semibold rounded px-7 py-[0.7rem] text-[1.02rem] transition-all"
              >
                Explore platform
              </Link>
            </div>
          </div>

          {/* visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: "easeOut" }}
          >
            <div className="relative h-[clamp(380px,52vw,540px)] flex items-center justify-center mt-10 lg:mt-0">
              <div className="relative w-full max-w-[520px] aspect-square mx-auto">
                {/* glow */}
                <motion.div
                  className="absolute inset-[12%] rounded-full blur-[8px]"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(201, 164, 92, 0.35) 0%, rgba(201, 164, 92, 0.08) 40%, transparent 70%)",
                  }}
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { opacity: [0.45, 0.9, 0.45], scale: [1, 1.06, 1] }
                  }
                  transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* constellation SVG */}
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox="0 0 520 520"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="goldLine"
                      x1="0"
                      y1="0"
                      x2="520"
                      y2="520"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#E4C988" stopOpacity="0.55" />
                      <stop
                        offset="0.5"
                        stopColor="#C9A45C"
                        stopOpacity="0.25"
                      />
                      <stop offset="1" stopColor="#12B89A" stopOpacity="0.4" />
                    </linearGradient>
                    <radialGradient id="nodeGold" cx="0.5" cy="0.5" r="0.5">
                      <stop stopColor="#E4C988" />
                      <stop offset="1" stopColor="#C9A45C" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <path
                    d="M80 140 L160 95 L250 120 L340 70 L440 130 L470 230 L420 320 L360 380 L250 410 L150 360 L90 280 Z"
                    stroke="url(#goldLine)"
                    strokeWidth="1.2"
                    opacity="0.55"
                  />
                  <path
                    d="M160 95 L210 200 L250 120 M340 70 L310 180 L420 320 M90 280 L210 200 L360 380 M440 130 L310 180 L250 410"
                    stroke="url(#goldLine)"
                    strokeWidth="0.9"
                    opacity="0.35"
                  />
                  <circle cx="80" cy="140" r="3.5" fill="#E4C988" />
                  <circle cx="160" cy="95" r="3" fill="#12B89A" />
                  <circle cx="250" cy="120" r="4" fill="#E4C988" />
                  <circle cx="340" cy="70" r="3" fill="#C9A45C" />
                  <circle cx="440" cy="130" r="3.5" fill="#12B89A" />
                  <circle cx="470" cy="230" r="3" fill="#E4C988" />
                  <circle cx="420" cy="320" r="3.5" fill="#C9A45C" />
                  <circle cx="360" cy="380" r="3" fill="#12B89A" />
                  <circle cx="250" cy="410" r="4" fill="#E4C988" />
                  <circle cx="150" cy="360" r="3" fill="#C9A45C" />
                  <circle cx="90" cy="280" r="3.5" fill="#12B89A" />
                  <circle cx="210" cy="200" r="2.5" fill="#E4C988" />
                  <circle cx="310" cy="180" r="2.5" fill="#12B89A" />
                  <motion.circle
                    cx="250"
                    cy="120"
                    r="14"
                    fill="url(#nodeGold)"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { opacity: [0.3, 0.6, 0.3] }
                    }
                    transition={{
                      duration: 3.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <motion.circle
                    cx="420"
                    cy="320"
                    r="16"
                    fill="url(#nodeGold)"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { opacity: [0.2, 0.45, 0.2] }
                    }
                    transition={{
                      duration: 3.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.6,
                    }}
                  />
                  <motion.circle
                    cx="150"
                    cy="360"
                    r="12"
                    fill="url(#nodeGold)"
                    animate={
                      shouldReduceMotion
                        ? undefined
                        : { opacity: [0.25, 0.5, 0.25] }
                    }
                    transition={{
                      duration: 3.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1.1,
                    }}
                  />
                </svg>

                {/* rings */}
                <div className="absolute inset-0 rounded-full border border-gold/[.28] pointer-events-none" />
                <motion.div
                  className="absolute inset-[9%] rounded-full border border-dashed pointer-events-none"
                  style={{ borderColor: "rgba(201, 164, 92, 0.18)" }}
                  animate={shouldReduceMotion ? undefined : { rotate: 360 }}
                  transition={{
                    duration: 50,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
                <motion.div
                  className="absolute inset-[20%] rounded-full border pointer-events-none"
                  style={{ borderColor: "rgba(18, 184, 154, 0.2)" }}
                  animate={shouldReduceMotion ? undefined : { rotate: -360 }}
                  transition={{
                    duration: 40,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
                <div
                  className="absolute inset-[32%] rounded-full border pointer-events-none"
                  style={{
                    borderColor: "rgba(201, 164, 92, 0.35)",
                    boxShadow:
                      "0 0 40px rgba(201, 164, 92, 0.12), inset 0 0 40px rgba(201, 164, 92, 0.06)",
                  }}
                />

                {/* orbit nodes */}
                <motion.span
                  className="absolute w-[10px] h-[10px] rounded-full top-[4%] left-1/2 -translate-x-1/2"
                  style={{
                    background: "#D4BA8E",
                    boxShadow: "0 0 14px rgba(228, 201, 136, 0.7)",
                  }}
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { y: [0, -8, 0], opacity: [0.75, 1, 0.75] }
                  }
                  transition={{
                    duration: 3.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.span
                  className="absolute w-[10px] h-[10px] rounded-full top-1/2 right-[3%] -translate-y-1/2"
                  style={{
                    background: "#12B89A",
                    boxShadow: "0 0 14px rgba(18,184,154,0.7)",
                  }}
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { x: [0, 8, 0], opacity: [0.75, 1, 0.75] }
                  }
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                />
                <motion.span
                  className="absolute w-[10px] h-[10px] rounded-full bottom-[6%] left-[22%]"
                  style={{
                    background: "#D4BA8E",
                    boxShadow: "0 0 14px rgba(228, 201, 136, 0.7)",
                  }}
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { y: [0, 8, 0], opacity: [0.75, 1, 0.75] }
                  }
                  transition={{
                    duration: 3.1,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
                <motion.span
                  className="absolute w-[7px] h-[7px] rounded-full top-[18%] left-[10%]"
                  style={{
                    background: "#12B89A",
                    boxShadow: "0 0 14px rgba(18,184,154,0.55)",
                  }}
                  animate={
                    shouldReduceMotion
                      ? undefined
                      : { scale: [1, 1.4, 1], opacity: [0.6, 1, 0.6] }
                  }
                  transition={{
                    duration: 2.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5,
                  }}
                />

                {/* logo */}
                <motion.div
                  className="absolute inset-[26%] flex items-center justify-center z-[3] rounded-full overflow-hidden"
                  style={{
                    background: "#F4F1EA",
                    border: "2px solid rgba(201, 164, 92, 0.65)",
                    boxShadow:
                      "0 0 0 12px rgba(7, 17, 31, 0.5), 0 28px 70px rgba(0, 0, 0, 0.5), 0 0 90px rgba(201, 164, 92, 0.28)",
                  }}
                  animate={shouldReduceMotion ? undefined : { y: [0, -10, 0] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  whileHover={shouldReduceMotion ? undefined : { scale: 1.04 }}
                >
                  <img
                    src="/assets/onedpdp-logo.png"
                    alt="OneDPDP company logo"
                    width="280"
                    height="280"
                    className="w-full h-full object-cover object-center relative z-[1]"
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
}
